import { useQuery } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import * as React from "react";
import { getChatAumPeriods, getOrganizations, getPerformanceFunds, getRequests } from "@/api/queries";
import {
  activateOfficeUserAtom,
  chatSelectionAtom,
  chatSessionAtom,
  organizationIdAtom,
  productAtom,
  requestAtom,
  resetChatAtom,
  updateChatSelectionAtom,
} from "@/state";
import { officeSelectionKey, publishOfficeSelection, validatedOfficeSelection } from "@/utils/chat-selection";
import type { User } from "@/types";

/** One validated source selection stays mounted for both worksheet formulas and chat. */
export function useOfficeConfiguration(user: User | undefined) {
  const userId = user?.id || user?.email || "";
  const selection = useAtomValue(chatSelectionAtom);
  const session = useAtomValue(chatSessionAtom);
  const activateUser = useSetAtom(activateOfficeUserAtom);
  const update = useSetAtom(updateChatSelectionAtom);
  const resetChat = useSetAtom(resetChatAtom);
  const setOrganizationId = useSetAtom(organizationIdAtom);
  const setProduct = useSetAtom(productAtom);
  const setRequest = useSetAtom(requestAtom);
  const previousSelection = React.useRef({ selection, session });

  React.useLayoutEffect(() => {
    const previous = previousSelection.current;
    if (JSON.stringify(previous.selection) !== JSON.stringify(selection) && previous.session === session) resetChat();
    previousSelection.current = { selection, session };
    activateUser(userId);
  }, [selection, session, resetChat, activateUser, userId]);

  const organizations = useQuery({
    queryKey: ["organizations", userId],
    queryFn: getOrganizations,
    enabled: !!userId,
  });
  const organization =
    selection.userId === userId ? organizations.data?.find((item) => item.id === selection.organizationId) : undefined;
  const organizationId = organization?.id ?? "";
  const isPerformance = selection.requestType === "performance";
  const isFirm = isPerformance && selection.scope === "firm";
  const isFund = isPerformance && selection.scope === "product";
  const isDueDiligence = selection.requestType === "due-diligence";
  const product = organization?.products.find((item) => item.id === selection.productId);
  const funds = useQuery({
    queryKey: [`chat-funds:${userId}`, { organizationId, productId: null }],
    queryFn: getPerformanceFunds,
    enabled: !!userId && !!organizationId && isFund,
  });
  const aumPeriods = useQuery({
    queryKey: [`chat-aum-periods:${userId}`, { organizationId }],
    queryFn: getChatAumPeriods,
    enabled: !!userId && !!organizationId && isFirm,
  });
  const requests = useQuery({
    queryKey: [`requests:${userId}`, { organizationId, productId: product?.id ?? "" }],
    queryFn: getRequests,
    enabled: !!userId && !!organizationId && isDueDiligence && !!product,
  });
  const fund = funds.data?.find((item) => item.id === selection.fundId && item.productId === selection.productId);
  const periods = isFirm ? aumPeriods.data : fund?.periods;

  React.useLayoutEffect(() => {
    if (
      userId &&
      selection.userId === userId &&
      organizations.isSuccess &&
      !organizations.isFetching &&
      !organization
    ) {
      update({ organizationId: organizations.data.length === 1 ? organizations.data[0].id : "" });
    }
  }, [
    userId,
    selection.userId,
    organizations.isSuccess,
    organizations.isFetching,
    organizations.data,
    organization,
    update,
  ]);

  // Background refresh of the same account/source does not interrupt running formulas.
  // A failed refresh, missing data, or a removed choice still invalidates the source immediately.
  const officeSelection = React.useMemo(
    () =>
      validatedOfficeSelection(selection, userId, {
        organizations: organizations.isSuccess ? organizations.data : undefined,
        requests: requests.isSuccess ? requests.data : undefined,
        funds: funds.isSuccess ? funds.data : undefined,
        aumPeriods: aumPeriods.isSuccess ? aumPeriods.data : undefined,
      }),
    [
      selection,
      userId,
      organizations.isSuccess,
      organizations.data,
      requests.isSuccess,
      requests.data,
      funds.isSuccess,
      funds.data,
      aumPeriods.isSuccess,
      aumPeriods.data,
    ]
  );
  const snapshot = officeSelectionKey(officeSelection);

  React.useLayoutEffect(() => {
    publishOfficeSelection(officeSelection);
    // Existing DDQ cards use these views; performance intentionally has no Kanban request.
    setOrganizationId(officeSelection?.organizationId ?? "");
    const ddq = officeSelection?.context.requestType === "due-diligence";
    setProduct(ddq ? (product ?? null) : null);
    const request = ddq ? requests.data?.find((item) => item.id === selection.requestId) : undefined;
    setRequest(request ?? null);
  }, [
    snapshot,
    officeSelection,
    product,
    requests.data,
    selection.requestId,
    setOrganizationId,
    setProduct,
    setRequest,
  ]);

  React.useLayoutEffect(() => () => publishOfficeSelection(null), []);

  return {
    userId,
    selection,
    update,
    organizations,
    organization,
    organizationId,
    product,
    funds,
    fund,
    aumPeriods,
    requests,
    periods,
    officeSelection,
    isPerformance,
    isFirm,
    isFund,
    isDueDiligence,
  };
}
